import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Input, Select, Tag, Row, Col, Statistic, message, Space, Checkbox, Divider, Progress, Alert } from 'antd';
import { PlusOutlined, CheckCircleOutlined, CloseCircleOutlined, LogoutOutlined } from '@ant-design/icons';
import apiService from 'services/api/apiService';
const { TextArea } = Input;
const fmt = (v: any) => '₦' + parseFloat(String(v || 0)).toLocaleString('en-NG', { minimumFractionDigits: 2 });

const exitTypeColors: Record<string,string> = { resignation:'blue', termination:'red', redundancy:'orange', retirement:'green', contract_end:'cyan', death:'default', abandonment:'volcano' };
const statusColors: Record<string,string> = { initiated:'default', in_progress:'processing', clearance_pending:'warning', completed:'success', cancelled:'error' };

const ExitManagement: React.FC = () => {
  const [exits, setExits] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [employees, setEmployees] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [createModal, setCreateModal] = useState(false);
  const [clearanceModal, setClearanceModal] = useState(false);
  const [selected, setSelected] = useState<any>(null);
  const [createForm] = Form.useForm();

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [er, xr, sr] = await Promise.all([apiService.getEmployees(), apiService.getExits(), apiService.getExitSummary()]);
      if (er.success) setEmployees(er.data || []);
      if (xr.success) setExits(xr.data || []);
      if (sr.success) setSummary(sr.data);
    } finally { setLoading(false); }
  };

  const handleCreate = async (values: any) => {
    const r = await apiService.initiateExit(values);
    if (r.success) {
      message.success(`Exit initiated. Final payment computed: ${fmt(r.computed?.total || 0)}`);
      setCreateModal(false); createForm.resetFields(); fetchAll();
    } else message.error(r.error || 'Failed');
  };

  const handleClearanceUpdate = async (field: string, value: number) => {
    const r = await apiService.updateExit({ id: selected.id, [field]: value });
    if (r.success) {
      setSelected((prev: any) => ({ ...prev, [field]: value }));
      fetchAll();
    }
  };

  const handleComplete = async () => {
    const r = await apiService.updateExit({ id: selected.id, status: 'completed', completed_date: new Date().toISOString().split('T')[0], final_salary_paid: 1 });
    if (r.success) { message.success('Exit process completed. Employee marked inactive.'); setClearanceModal(false); fetchAll(); }
    else message.error(r.error || 'Failed');
  };

  const clearanceItems = [
    { key: 'clearance_it', label: 'IT & Systems Access' },
    { key: 'clearance_finance', label: 'Finance & Accounts' },
    { key: 'clearance_admin', label: 'Administration' },
    { key: 'clearance_security', label: 'Security & Access Cards' },
    { key: 'clearance_hr', label: 'HR Records & Documentation' },
    { key: 'equipment_returned', label: 'Equipment Returned' },
    { key: 'access_revoked', label: 'System Access Revoked' },
  ];

  return (
    <div style={{ padding: 24 }}>
      <h1>Exit Management & Offboarding</h1>
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={4}><Card><Statistic title="Total Exits" value={summary?.total || 0} /></Card></Col>
        <Col span={4}><Card><Statistic title="Resignations" value={summary?.resignations || 0} valueStyle={{ color: '#1890ff' }} /></Card></Col>
        <Col span={4}><Card><Statistic title="Terminations" value={summary?.terminations || 0} valueStyle={{ color: '#ff4d4f' }} /></Card></Col>
        <Col span={4}><Card><Statistic title="In Progress" value={summary?.in_progress || 0} valueStyle={{ color: '#faad14' }} /></Card></Col>
        <Col span={4}><Card><Statistic title="Exit Interviews" value={summary?.interviews_done || 0} valueStyle={{ color: '#52c41a' }} /></Card></Col>
        <Col span={4}><Card><Statistic title="Avg Final Pay" value={summary?.avg_final_payment || 0} prefix="₦" formatter={(v: any) => Number(v).toLocaleString()} /></Card></Col>
      </Row>

      <Card extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModal(true)}>Initiate Exit</Button>}>
        <Table dataSource={exits} rowKey="id" loading={loading} pagination={{ pageSize: 10 }}
          columns={[
            { title: 'Employee', dataIndex: 'employee_name', render: (v: string, r: any) => <><div style={{ fontWeight: 600 }}>{v}</div><div style={{ fontSize: 11, color: '#999' }}>{r.department} • {r.position}</div></> },
            { title: 'Exit Type', dataIndex: 'exit_type', render: (v: string) => <Tag color={exitTypeColors[v] || 'default'}>{v?.replace(/_/g,' ')}</Tag> },
            { title: 'Last Working Day', dataIndex: 'last_working_date' },
            { title: 'Clearance', dataIndex: 'clearance_pct', render: (v: number) => <Progress percent={Number(v) || 0} size="small" strokeColor={v >= 100 ? '#52c41a' : '#faad14'} /> },
            { title: 'Final Payment', dataIndex: 'total_final_payment', render: fmt },
            { title: 'Interview', dataIndex: 'exit_interview_done', render: (v: number) => v ? <Tag color="success">Done</Tag> : <Tag color="warning">Pending</Tag> },
            { title: 'Status', dataIndex: 'status', render: (v: string) => <Tag color={statusColors[v] || 'default'}>{v?.replace(/_/g,' ')}</Tag> },
            { title: 'Actions', render: (_: any, r: any) => (
              <Space>
                <Button size="small" onClick={() => { setSelected(r); setClearanceModal(true); }}>Clearance</Button>
                {!r.exit_interview_done && <Button size="small" onClick={async () => {
                  await apiService.updateExit({ id: r.id, exit_interview_done: 1, exit_interview_notes: 'Completed' });
                  fetchAll();
                  message.success('Exit interview marked done');
                }}>Mark Interview Done</Button>}
              </Space>
            )},
          ]} />
      </Card>

      {/* Create Exit Modal */}
      <Modal title="Initiate Exit Process" open={createModal} onCancel={() => { setCreateModal(false); createForm.resetFields(); }} footer={null} width={650}>
        <Alert message="Initiating exit will automatically compute gratuity and leave balance payout based on employee records." type="info" showIcon style={{ marginBottom: 16 }} />
        <Form form={createForm} layout="vertical" onFinish={handleCreate}>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="employee_id" label="Employee" rules={[{ required: true }]}><Select showSearch optionFilterProp="children">{employees.map(e => <Select.Option key={e.id} value={e.id}>{e.first_name} {e.last_name} — {e.department}</Select.Option>)}</Select></Form.Item></Col>
            <Col span={12}><Form.Item name="exit_type" label="Exit Type" rules={[{ required: true }]}>
              <Select>{Object.keys(exitTypeColors).map(t => <Select.Option key={t} value={t}>{t.replace(/_/g,' ')}</Select.Option>)}</Select>
            </Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="notice_date" label="Notice Date"><Input type="date" /></Form.Item></Col>
            <Col span={12}><Form.Item name="last_working_date" label="Last Working Date" rules={[{ required: true }]}><Input type="date" /></Form.Item></Col>
          </Row>
          <Form.Item name="exit_reason" label="Reason for Exit"><TextArea rows={3} /></Form.Item>
          <Form.Item name="notes" label="Notes"><TextArea rows={2} /></Form.Item>
          <Space><Button type="primary" htmlType="submit">Initiate Exit</Button><Button onClick={() => setCreateModal(false)}>Cancel</Button></Space>
        </Form>
      </Modal>

      {/* Clearance Checklist Modal */}
      <Modal title={`Exit Clearance — ${selected?.employee_name || ''}`} open={clearanceModal} onCancel={() => setClearanceModal(false)}
        footer={[
          <Button key="complete" type="primary" icon={<CheckCircleOutlined />}
            disabled={(selected?.clearance_pct || 0) < 100}
            onClick={handleComplete}>Mark Exit Complete</Button>,
          <Button key="close" onClick={() => setClearanceModal(false)}>Close</Button>
        ]} width={560}>
        {selected && (
          <div>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={12}><Statistic title="Gratuity" value={selected.gratuity_amount || 0} prefix="₦" formatter={(v: any) => Number(v).toLocaleString()} /></Col>
              <Col span={12}><Statistic title="Leave Payout" value={selected.leave_balance_payout || 0} prefix="₦" formatter={(v: any) => Number(v).toLocaleString()} /></Col>
            </Row>
            <Statistic title="Total Final Payment" value={selected.total_final_payment || 0} prefix="₦" formatter={(v: any) => Number(v).toLocaleString()} valueStyle={{ color: '#1890ff', fontSize: 24 }} />
            <Divider>Clearance Checklist</Divider>
            <Progress percent={Number(selected.clearance_pct) || 0} style={{ marginBottom: 16 }} strokeColor={selected.clearance_pct >= 100 ? '#52c41a' : '#faad14'} />
            {clearanceItems.map(item => (
              <div key={item.key} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                <span>{item.label}</span>
                <Checkbox checked={!!selected[item.key]} onChange={e => handleClearanceUpdate(item.key, e.target.checked ? 1 : 0)}>
                  {selected[item.key] ? <Tag color="success">Done</Tag> : <Tag color="warning">Pending</Tag>}
                </Checkbox>
              </div>
            ))}
            <Divider>Exit Interview</Divider>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Exit Interview Completed</span>
              <Checkbox checked={!!selected.exit_interview_done} onChange={e => handleClearanceUpdate('exit_interview_done', e.target.checked ? 1 : 0)}>
                {selected.exit_interview_done ? <Tag color="success">Done</Tag> : <Tag color="warning">Pending</Tag>}
              </Checkbox>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};
export default ExitManagement;
